import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { OpenXLogo } from '../../../assets/icons';
import bgImage from '../../../assets/webp/SignInBGImage.webp';
import { ButtonComponent } from '../../components/button';
import { useResendVerificationMutation, useVerifyUserMutation } from '../../../redux/services/authApi';
import styles from './style.module.css';

export const VerifyUser: React.FC = () => {
  // If you plan to toggle this later, make it a useState<boolean>.
  // const showOTP = true;

  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState<string>('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [verifyUser] = useVerifyUserMutation();
  const [resendVerification, { isLoading: isResending }] = useResendVerificationMutation();
  
  
  
  const RESEND_TIMER_DURATION = 10 * 60;
  const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMER_DURATION);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to format time for display (MM:SS)
  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Start/Reset Timer function
  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSecondsLeft(RESEND_TIMER_DURATION); // Reset to full duration

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []); // No dependencies for startTimer, it always resets to RESEND_TIMER_DURATION

  // Effect to manage the timer lifecycle
  useEffect(() => {
    startTimer(); // Start timer on component mount

    return () => {
      // Cleanup: Clear interval when component unmounts
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [startTimer]);


  // Extract email from location state when the component mounts
  useEffect(() => {
    
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      setEmail(urlParams.get('email') as string);
      console.log('Received email for verification:', urlParams.get('email'));

  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    // keep only digits and take the last typed char
    const raw = e.target.value.replace(/\D/g, '');
    const char = raw.slice(-1); // last digit or ""

    setOtp((prev) => {
      const copy = [...prev];
      copy[index] = char ?? '';
      return copy;
    });

    if (char && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;

    setOtp((prev) => {
      const copy = [...prev];
      let i = index;
      for (const d of pasted.slice(0, 6 - index)) {
        copy[i] = d;
        i++;
      }
      // focus next empty or last filled
      const nextIndex = Math.min(index + pasted.length, 5);
      setTimeout(() => inputRefs.current[nextIndex]?.focus(), 0);
      return copy;
    });
  };

  const instructiontext = ' We’ve sent a One-Time Password (OTP) to your registered email. Enter the code below to verify your account.';

  const handleVerify = async (e: React.MouseEvent<HTMLButtonElement>) => {


    if(isLoading)
    {
      console.log("Is Processing");
      return;
    }

    setIsLoading(true);

    const code = otp.join('');
    console.log('OTP Entered:', code);
    console.log('Email Retrieved:', email);

    e.preventDefault();
    setApiError(null);


    if (!code || code.length !== 6 || !/^\d+$/.test(code)) {
      // Checks for exactly 6 digits
      setApiError('Please enter the complete 6-digit OTP.');
      console.log(apiError);
      setIsLoading(false); // Stop loading if validation fails
      return; // Exit the function
    }

    if (!email) {
      setApiError('Email is missing. Please return to the registration page.');
      console.log(apiError);
    setIsLoading(false);

      return;
    }

    try {
      const payload = await verifyUser({ email, code }).unwrap();

      // this will come in later later
      // setTokenItem(TOKEN_KEY.AUTH_TOKEN, payload.access_token);
      // setTokenItem(TOKEN_KEY.REFRESH_TOKEN, payload.refresh_token);

      navigate('/marketPlace');

    } catch (err: any) {
      console.error('Failed to log in:', err);
      const errorMessage = err.data?.message || 'Invalid credentials. Please try again.';
      setApiError(errorMessage);
    }
    finally
    {
    setIsLoading(false);
      
    }

  };


  const handleResend = async () => {

    if (secondsLeft > 0 || isResending) {
      console.log("Resend is currently disabled or in progress.");
      return;
    }

    setApiError(null);

    if (!email) {
      setApiError('Email is missing. Please return to the registration page.');
      console.log('Error: Email missing for resend.');
      return;
    }

    try {
      await resendVerification({ email }).unwrap();
      startTimer(); 
      console.log('Verification email resent successfully!');
    } catch (err: any) {
      console.error('Failed to resend verification:', err);
      const errorMessage = err.data?.message || err.error || 'Failed to resend verification. Please try again.';
      setApiError(errorMessage);
    }
  };

  const resendText = secondsLeft > 0 ? `Resend in ${formatTime(secondsLeft)}` : isResending ? 'Resending...' : 'Resend';
  const isResendDisabled = secondsLeft > 0 || isResending;

  return (
    <div className={styles.container}>
      <img className={styles.bgImg} src={bgImage} alt="Gallery Background" />
      <div className={styles.entryContainer}>
        <OpenXLogo className={styles.icon} />
        <div className={styles.headerTxt}>Verify Your Account</div>

        <div className={styles.alignDiv}>
          <div className={styles.instr}>{instructiontext}</div>

        <div className={styles.otpCont}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              className={styles.otpInput}
            />
          ))}
        </div>
          <div className={styles.errMsg}>{apiError}</div>

        <div className={styles.resendCont}>
          Didn’t get an email?
          <div
            className={`${styles.resend} ${isResendDisabled ? styles.resendDisabled : ''}`}
            onClick={handleResend}
          >
            {resendText}
          </div>
        </div>


        <ButtonComponent 
        buttonName={isLoading ? 'Verifying...' : 'Verify'}
        style={{ 
          marginTop: 40, 
          marginBottom: 36, 
          border: 0,
          width: '100%', 
          height: '46px',
          opacity: isLoading ? 0.5 : 1  }} handleclick={handleVerify} />
        </div>


      </div>
    </div>
  );
};
